import React, { useState, useEffect, useRef } from 'react';
import './styles.scss';

const TIMEOUTS = [0, 2000, 5000, 5000];

export const Thumbnail: React.FC<
  {
    src: string;
    alt: string;
    fallback?: JSX.Element;
    retry?: boolean;
    // Проявлять картинку по загрузке вместо мгновенной подстановки. Нужно там, где на
    // экране разом появляются десятки тумбнейлов (сетка ассетов): без этого они
    // «выстреливают» вразнобой по мере декодирования.
    fadeIn?: boolean;
  } & Partial<React.ImgHTMLAttributes<HTMLImageElement>>
> = ({ src, alt, fallback = null, retry = true, fadeIn = false, ...props }) => {
  const [attemptNumber, setAttemptNumber] = useState(0);
  const [loadState, setLoadState] = useState<'ready' | 'recovering' | 'failed'>(
    retry ? 'recovering' : 'ready',
  );
  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [shown, setShown] = useState(false);

  // If the component will unmount, we need to clear timeout
  useEffect(() => {
    return () => {
      timeoutId !== null && clearTimeout(timeoutId);
    };
  }, [timeoutId]);

  // If the component receives new src, we need to restart attempts
  useEffect(() => {
    timeoutId !== null && clearTimeout(timeoutId);
    setAttemptNumber(0);
    setShown(false);
    if (retry) {
      setLoadState('recovering');
      setTimeoutId(null);
    }
  }, [src, retry]); // eslint-disable-line react-hooks/exhaustive-deps

  // Картинка из кэша браузера может оказаться готовой ещё до того, как мы навесим onLoad,
  // и события мы не дождёмся — проверяем complete на самом узле. Именно эффектом на [src],
  // а не ref-колбэком: колбэк со стабильной ссылкой React повторно не вызывает, поэтому
  // при смене src на том же узле сброшенное выше shown уже никто бы не вернул.
  const imgRef = useRef<HTMLImageElement | null>(null);
  useEffect(() => {
    if (imgRef.current?.complete) {
      setShown(true);
    }
  }, [src]);

  const onError = () => {
    // Increase attempt number
    const newAttemptNumber = attemptNumber + 1;
    setAttemptNumber(newAttemptNumber);

    // Set error to remove img from DOM
    setLoadState('failed');

    // If there are available attempts, set timeout to unset error and place img in the DOM
    if (newAttemptNumber < TIMEOUTS.length && retry) {
      const timeoutId = setTimeout(() => {
        setLoadState('recovering');
      }, TIMEOUTS[newAttemptNumber]);
      setTimeoutId(timeoutId);
    }
  };

  const style: React.CSSProperties = { ...props.style };
  if (loadState !== 'ready') {
    style.display = 'none';
  }

  // Класс проявления навешиваем только при fadeIn: он ненужен остальным тумбнейлам
  // приложения, а неймспейс защищает от совпадения с глобальными стилями.
  const className = [
    props.className,
    fadeIn ? 'aa-thumbnail_fade-in' : '',
    fadeIn && shown ? 'aa-thumbnail_shown' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {loadState !== 'ready' && fallback}
      {loadState !== 'failed' && (
        <img
          {...props}
          ref={imgRef}
          className={className || undefined}
          style={style}
          src={src}
          alt={alt}
          onError={onError}
          onLoad={() => {
            setLoadState('ready');
            setShown(true);
          }}
        />
      )}
    </>
  );
};
